/**
 * The body of an E2E evidence row: the run's status badge, its autoplaying video
 * and the test written out as readable steps. For a UI test it also shows the
 * base-versus-head visual checkpoints.
 *
 * @module features/proofs/components/E2eEvidenceBody
 */
import React from 'react';
import { Markdown, StatusBadge, Text, normalizeRunStatus } from '@chatsdet/ui';
import { useWorkflowSteps } from '../hooks/useWorkflowSteps';
import type { ProofEvidence } from '../types';
import { EvidenceVideo } from './EvidenceVideo';
import { UiTestCheckpoints } from './UiTestCheckpoints';

type E2e = Extract<ProofEvidence, { kind: 'e2e' }>;

export const E2eEvidenceBody: React.FC<{ item: E2e; expanded: boolean }> = ({ item, expanded }) => {
  const steps = useWorkflowSteps(item.workflowId, expanded);
  const { run } = item;
  return (
    <div className="space-y-4">
      {run ? (
        <>
          <StatusBadge status={normalizeRunStatus(run.status)} size="sm" />
          <EvidenceVideo run={run} nodeId={item.nodeId} />
        </>
      ) : (
        <Text color="muted">This run is no longer available.</Text>
      )}
      {item.ui && <UiTestCheckpoints ui={item.ui} />}
      {steps.isError ? (
        <Text color="muted">{steps.error.message}</Text>
      ) : steps.data ? (
        <Markdown className="prose prose-sm max-w-none">{steps.data.markdown}</Markdown>
      ) : (
        <Text color="muted">Loading steps...</Text>
      )}
    </div>
  );
};
